import { useEffect, useState } from 'react';
import { Panel } from './Panel';
import { ResourceBar } from './ResourceBar';
import { formatNumber } from '@/lib/utils';
import type { Character } from '@/types/database';

interface VitalsPanelProps {
  character: Character;
}

function computeTimer(
  lastRegenAt: string | null,
  intervalMinutes: number,
  current: number,
  max: number,
): string {
  if (current >= max) return 'Full';
  if (!lastRegenAt) return '';
  const elapsed = (Date.now() - new Date(lastRegenAt).getTime()) / 1000;
  const intervalSec = intervalMinutes * 60;
  const remaining = intervalSec - (elapsed % intervalSec);
  if (remaining <= 0) return '+1 soon';
  const mins = Math.floor(remaining / 60);
  const secs = Math.floor(remaining % 60);
  return `+1 in ${mins}:${secs.toString().padStart(2, '0')}`;
}

function computeHappyTimer(character: Character): string {
  if (character.happy === 200) return '';
  if (character.happy > 200) return 'Decaying';
  if (character.happy >= 200) return '';
  return computeTimer(character.happy_last_decay_at, 10, character.happy, 200);
}

export function VitalsPanel({ character }: VitalsPanelProps): JSX.Element {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Suppress unused warning — tick drives re-render for timer computation.
  void tick;

  const hpTimer = computeTimer(character.hp_last_regen_at, 2, character.hp, character.hp_max);
  const energyTimer = computeTimer(
    character.energy_last_regen_at,
    3,
    character.energy,
    character.energy_max,
  );
  const nerveTimer = computeTimer(
    character.nerve_last_regen_at,
    5,
    character.nerve,
    character.nerve_max,
  );
  const happyTimer = computeHappyTimer(character);

  return (
    <Panel
      title="Vitals"
      action={
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="text-candle-dark">
            &#9788;
          </span>
          <span className="font-mono text-sm text-ink">{formatNumber(character.happy)}</span>
          <span className="text-ink-light text-xs">{happyTimer ? `(${happyTimer})` : 'Happy'}</span>
        </span>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <ResourceBar
          label="HP"
          current={character.hp}
          max={character.hp_max}
          fillClass="bg-blood-bright"
          timer={hpTimer}
        />
        <ResourceBar
          label="Energy"
          current={character.energy}
          max={character.energy_max}
          fillClass="bg-candle"
          timer={energyTimer}
        />
        <ResourceBar
          label="Nerve"
          current={character.nerve}
          max={character.nerve_max}
          fillClass="bg-sea-light"
          timer={nerveTimer}
        />
      </div>
    </Panel>
  );
}
